import { uniReactRoot } from '@affine/component';
import {
  type CellRenderProps,
  createIcon,
  type DataViewCellLifeCycle,
  EditorHostKey,
} from '@blocksuite/affine/blocks/database';
import {
  UserListProvider,
  UserProvider,
  WriterInfoProvider,
} from '@blocksuite/affine/shared/services';
import { css } from '@emotion/css';
import {
  forwardRef,
  type ForwardRefRenderFunction,
  type ReactNode,
  useImperativeHandle,
} from 'react';

import { useSignalValue } from '../../../../modules/doc-info/utils';
import { UserAvatarLabel } from '../shared/user-avatar-label';
import { updatedByPropertyModelConfig } from './define';

const cellContainer = css({
  width: '100%',
  position: 'relative',
  gap: '6px',
  display: 'flex',
  flexWrap: 'wrap',
  // Stays `visible` so the avatar's outer outline ring (painted by
  // the Avatar component via CSS `outline`, which lives OUTSIDE
  // the element's border-box) isn't amputated at the cell edge.
  // Name truncation is handled on the name column itself, not by
  // clipping the row.
  overflow: 'visible',
  minWidth: 0,
});

const UpdatedByCellComponent: ForwardRefRenderFunction<
  DataViewCellLifeCycle,
  CellRenderProps<{}, string | null, string | null>
> = (props, ref): ReactNode => {
  useImperativeHandle(
    ref,
    () => ({
      beforeEnterEditMode: () => {
        return false;
      },
      beforeExitEditingMode: () => {},
      afterEnterEditingMode: () => {},
      focusCell: () => true,
      blurCell: () => true,
      forceUpdate: () => {},
    }),
    []
  );
  const host = props.cell.view.serviceGet(EditorHostKey);
  const userService = host?.std.getOptional(UserProvider);
  const userListService = host?.std.getOptional(UserListProvider);
  const writerInfo =
    host?.std.getOptional(WriterInfoProvider)?.getWriterInfo() ?? null;
  const memberId = useSignalValue(props.cell.value$);
  if (!memberId) {
    return null;
  }
  return (
    <div className={cellContainer}>
      <UserAvatarLabel
        key={memberId}
        memberId={memberId}
        userService={userService}
        userListService={userListService}
        writerInfo={writerInfo}
      />
    </div>
  );
};

const UpdatedByCell = forwardRef(UpdatedByCellComponent);

export const updatedByPropertyConfig =
  updatedByPropertyModelConfig.createPropertyMeta({
    icon: createIcon('MemberIcon'),
    cellRenderer: {
      view: uniReactRoot.createUniComponent(UpdatedByCell),
    },
  });
